/*
 * Contact form — dynamic fields.
 * Shows/hides and toggles required state of extra fields based on the selected
 * inquiry type ("Objet de la demande"). Driven by a per-type matrix passed on
 * the <contact-dynamic-form data-matrix> attribute, keyed by the selected
 * option's data-index. Field states: 'req' (required), 'opt' (optional), 'off'
 * (hidden + disabled so it isn't submitted).
 *
 * Some request types don't expand fields at all: picking one navigates to
 * that request's own page, landing on its form section (client req).
 * Those come in on data-redirects as a map keyed by the same data-index the
 * matrix uses — not by label, which would break as soon as the options are
 * translated. The matrix still carries their field rules, so removing a
 * redirect restores the inline behaviour.
 */
class ContactDynamicForm extends HTMLElement {
  connectedCallback() {
    try {
      this.matrix = JSON.parse(this.dataset.matrix || '[]');
    } catch (e) {
      this.matrix = [];
    }

    try {
      this.redirects = JSON.parse(this.dataset.redirects || '{}');
    } catch (e) {
      this.redirects = {};
    }

    this.select = this.querySelector('[data-cform-subject]');
    this.fields = Array.from(this.querySelectorAll('[data-cform-field]'));
    if (!this.select) return;

    this.select.addEventListener('change', () => {
      // A redirect type navigates away, so don't bother reshuffling fields.
      if (this.redirectFor(this.select.selectedOptions[0])) return;
      this.apply();
    });
    // Apply on load so a browser-restored selection renders correctly.
    this.apply();
  }

  // Navigates in the current tab. The URL carries the target section's anchor,
  // so the visitor lands on the form rather than the top of the page. Only
  // same-origin paths are honoured, so a bad setting can't send visitors
  // off-site.
  //
  // Not window.open: with 'noopener' in the features string it returns null
  // even when the tab opened fine, so any `if (!opened)` fallback fires too and
  // the page ends up loading twice — once in each tab.
  redirectFor(option) {
    const url = option && this.redirects[option.dataset.index];
    if (!url) return false;
    let target;
    try {
      target = new URL(url, window.location.origin);
    } catch (e) {
      return false;
    }
    if (target.origin !== window.location.origin) return false;
    window.location.assign(target.href);
    return true;
  }

  apply() {
    const option = this.select.selectedOptions[0];
    const index = option ? parseInt(option.dataset.index, 10) : NaN;
    const config = Number.isInteger(index) ? this.matrix[index] : null;

    this.fields.forEach((field) => {
      const key = field.dataset.key;
      const state = config ? config[key] : 'off';
      const input = field.querySelector('[data-cform-input]');
      const req = field.querySelector('[data-cform-req]');

      if (!config || state === 'off' || state == null) {
        field.hidden = true;
        if (input) {
          input.disabled = true;
          input.required = false;
          if (input.tagName === 'SELECT') {
            input.selectedIndex = 0;
          } else {
            input.value = '';
          }
        }
        if (req) req.hidden = true;
      } else {
        field.hidden = false;
        if (input) {
          input.disabled = false;
          input.required = state === 'req';
        }
        if (req) req.hidden = state !== 'req';
      }
    });
  }
}

if (!customElements.get('contact-dynamic-form')) {
  customElements.define('contact-dynamic-form', ContactDynamicForm);
}
